import type { FC } from "react";
import { Platform, Pressable } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../../theme";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import type { CountryOption } from "../phone-number";
import { getFlagEmoji } from "../phone-number";

interface CountryTriggerProps {
  readonly option: CountryOption;
  readonly isOpen: boolean;
  readonly disabled: boolean;
  readonly onPress: () => void;
}

/** The flag + dial code button sitting inside the field, before the number. */
const CountryTrigger: FC<CountryTriggerProps> = ({ option, isOpen, disabled, onPress }) => {
  const { theme } = useUnistyles();
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      hitSlop={{ top: 8, bottom: 8 }}
      accessibilityRole="button"
      accessibilityLabel={`Country code, ${option.name} +${option.callingCode}`}
      accessibilityHint="Opens the list of countries"
      accessibilityState={{ disabled, expanded: isOpen }}
      style={[styles.trigger, Platform.OS === "web" && !disabled && styles.webInteractive]}
    >
      <Text textStyle="md/regular">{getFlagEmoji(option.country)}</Text>
      <Text textStyle="sm/regular" color="text">
        +{option.callingCode}
      </Text>
      <Icon name={isOpen ? "chevronUp" : "chevronDown"} size={16} color={theme.colors.textMuted} />
    </Pressable>
  );
};

CountryTrigger.displayName = "CountryTrigger";

export { CountryTrigger };

const styles = StyleSheet.create((theme) => ({
  trigger: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "stretch",
    gap: theme.spacing.xs,
    paddingLeft: theme.spacing["3xl"],
    paddingRight: theme.spacing.md,
    marginRight: theme.spacing.sm,
    borderRightWidth: 1,
    borderRightColor: theme.colors.field.border_default,
  },
  webInteractive: {
    cursor: "pointer",
    userSelect: "none",
  },
}));
